export const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '')

// A list that comma-joining can't carry back — a blank item, or an item
// containing a comma — is shown as JSON instead, so saving it untouched or
// appending to it keeps every item.
const joinsCleanly = (items) => items.every((item) => String(item).trim() !== '' && !String(item).includes(','))

// Config values are JSON: arrays show comma-separated, everything else as-is.
export const formatConfigValue = (value) => {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return joinsCleanly(value) ? value.join(', ') : JSON.stringify(value)
  return String(value)
}

const parseJsonArray = (text) => {
  try {
    const parsed = JSON.parse(text)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

const NUMERIC_TYPES = ['number', 'integer', 'percent']
const TRUE_WORDS = ['true', 'yes', '1']
const FALSE_WORDS = ['false', 'no', '0']

// Why `raw` can't be stored for `datatype`, or null when it can (blank is
// allowed here; callers decide whether a value is required).
export const configValueError = (raw, datatype) => {
  const text = String(raw ?? '').trim()
  if (text === '') return null

  if (NUMERIC_TYPES.includes(datatype)) {
    const number = Number(text)
    if (!Number.isFinite(number)) return `"${text}" is not a number`
    if (datatype === 'integer' && !Number.isInteger(number)) return `"${text}" is not a whole number`
  }
  if (datatype === 'boolean' && ![...TRUE_WORDS, ...FALSE_WORDS].includes(text.toLowerCase())) {
    return `"${text}" is not true or false`
  }
  if (datatype === 'array' && text.startsWith('[') && !parseJsonArray(text)) {
    return 'is not a valid list: fix the JSON, or use comma-separated values'
  }
  return null
}

// Turns what an admin typed into the JSON value the API stores, following
// the key's datatype. Returns undefined for a blank input. Check
// configValueError first: invalid input is not coerced into a valid value.
export const parseConfigValue = (raw, datatype) => {
  const text = String(raw ?? '').trim()
  if (text === '') return undefined

  if (datatype === 'array') {
    if (text.startsWith('[')) return parseJsonArray(text)
    return text.split(',').map((item) => item.trim()).filter(Boolean)
  }
  if (datatype === 'boolean') return TRUE_WORDS.includes(text.toLowerCase())
  if (NUMERIC_TYPES.includes(datatype)) return Number(text)
  return text
}

// Whether what an admin typed would store the value already saved, so an edit
// typed back to its original isn't counted as a change.
export const configValueUnchanged = (raw, stored, datatype) => {
  if (configValueError(raw, datatype)) return false
  const parsed = parseConfigValue(raw, datatype)
  if (parsed === undefined) return stored === null || stored === undefined
  return JSON.stringify(parsed) === JSON.stringify(stored)
}
